const ISO_DATE_ONLY_RE = /^(\d{4})-(\d{2})-(\d{2})$/;
const ISO_DATE_PREFIX_RE = /^(\d{4}-\d{2}-\d{2})(?:$|T)/;

function validDate(date) {
  return date instanceof Date && !Number.isNaN(date.getTime());
}

function calendarDateFromMatch(match) {
  const year = Number(match[1]);
  const monthIndex = Number(match[2]) - 1;
  const day = Number(match[3]);
  const date = new Date(year, monthIndex, day);

  if (
    date.getFullYear() !== year
    || date.getMonth() !== monthIndex
    || date.getDate() !== day
  ) {
    return null;
  }
  return date;
}

/**
 * Parse a value for date-only display without letting JavaScript reinterpret a
 * YYYY-MM-DD calendar date as UTC midnight. Datetimes remain real instants and
 * therefore continue to display in the viewer's local timezone.
 */
export function parseDateValue(value) {
  if (value instanceof Date) {
    return validDate(value) ? new Date(value.getTime()) : null;
  }
  if (value === null || value === undefined || value === '') return null;

  const text = String(value).trim();
  const dateOnly = ISO_DATE_ONLY_RE.exec(text);
  if (dateOnly) return calendarDateFromMatch(dateOnly);

  const parsed = new Date(text);
  return validDate(parsed) ? parsed : null;
}

export function formatDateValue(
  value,
  {
    locale,
    fallback = '—',
    invalidFallback,
    ...options
  } = {},
) {
  if (value === null || value === undefined || value === '') return fallback;
  const date = parseDateValue(value);
  if (!date) return invalidFallback ?? String(value);
  return date.toLocaleDateString(locale, options);
}

/**
 * Format a calendar field by its literal ISO day. UTC is intentional here: it
 * makes 1995-12-28 display as December 28 in every viewer timezone.
 */
export function formatCalendarDateValue(
  value,
  {
    locale,
    fallback = '—',
    invalidFallback,
    ...options
  } = {},
) {
  if (value === null || value === undefined || value === '') return fallback;
  const inputValue = toDateInputValue(value);
  const match = ISO_DATE_ONLY_RE.exec(inputValue);
  if (!match) return invalidFallback ?? String(value);

  const date = new Date(Date.UTC(
    Number(match[1]),
    Number(match[2]) - 1,
    Number(match[3]),
  ));
  return date.toLocaleDateString(locale, { ...options, timeZone: 'UTC' });
}

/** Display an ISO calendar date as "Aug 4 2026", independent of locale. */
export function formatDisplayDate(value, { fallback = '—' } = {}) {
  return formatCalendarDateValue(value, {
    locale: 'en-US', fallback, year: 'numeric', month: 'short', day: 'numeric',
  }).replace(',', '');
}

export function formatDateTimeValue(
  value,
  {
    locale,
    fallback = '—',
    invalidFallback,
    ...options
  } = {},
) {
  if (value === null || value === undefined || value === '') return fallback;
  const date = parseDateValue(value);
  if (!date) return invalidFallback ?? String(value);
  return date.toLocaleString(locale, options);
}

export function toDateInputValue(value) {
  if (value === null || value === undefined || value === '') return '';

  const text = String(value).trim();
  const prefixedDate = ISO_DATE_PREFIX_RE.exec(text);
  const validPrefixedValue = prefixedDate
    && parseDateValue(prefixedDate[1])
    && (text === prefixedDate[1] || parseDateValue(text));
  if (validPrefixedValue) return prefixedDate[1];

  const date = parseDateValue(value);
  return date ? localDateInputValue(date) : '';
}

export function localDateInputValue(date = new Date()) {
  if (!validDate(date)) return '';
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

export function toDateTimeLocalInputValue(value) {
  const date = parseDateValue(value);
  if (!date) return '';
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  return `${localDateInputValue(date)}T${hours}:${minutes}`;
}

export function dateTimeLocalToIsoValue(value) {
  if (!value) return '';
  const date = new Date(value);
  return validDate(date) ? date.toISOString() : '';
}

export function addCalendarDays(value, amount) {
  const date = parseDateValue(value);
  if (!date || !Number.isFinite(amount)) return '';
  date.setDate(date.getDate() + amount);
  return localDateInputValue(date);
}
